import React, { createContext, useState, useContext, useReducer } from 'react';
import App from 'antd/lib/app';
import { RcFile } from 'antd/lib/upload';
import { some, sortBy } from 'lodash';
import { readFile } from 'src/domain/readFile';
import { ImportView, reducer, ViewState } from './importReducer';
import { parseCsv, ParsedRecord } from './ImportParser';

export interface IImportContext<T extends string> {
    viewState: ViewState<T>;
    importProgress: ImportProgress;
    setImportProgress: (importProgress: ImportProgress) => void;
    importErrors: string[];
    setImportErrors: (importErrors: string[]) => void;
    importHasErrors: boolean;
    isReadyToImport: boolean;
    csvConfig: Readonly<IImportCsvConfig[]>;
    onFileLoad(file: RcFile): void;
    onFileRemove(): void;
    onImportSubmit(): void;
    onImportComplete(): void;
}

const initialViewState: ViewState<any> = {
    view: ImportView.INITIAL,
    records: [],
    file: null,
};

export const ImportContext = createContext<IImportContext<any>>({
    viewState: initialViewState,
    importProgress: { imported: 0, failed: 0, total: 0 },
    setImportProgress: () => {},
    importErrors: [],
    importHasErrors: false,
    isReadyToImport: false,
    csvConfig: [],
    setImportErrors: () => {},
    onFileLoad: () => {},
    onFileRemove: () => {},
    onImportSubmit: () => {},
    onImportComplete: () => {},
});

export type ValidationResult = { error: string } | { warning: string } | undefined;

export interface IImportCsvConfig {
    column: string;
    required: boolean;
    description: string;
    width?: number;
}

export interface ImportProgress {
    imported: number;
    failed: number;
    total: number;
}

interface ImportProviderProps<T extends string> {
    csvConfig: Readonly<IImportCsvConfig[]>;
    skipHeaderRows?: number; // how many, default = 1, first rows to skip (they are headers)
    parser: (row: ParsedRecord<T>) => ParsedRecord<T>;
    children?: React.ReactNode;
}

export function ImportProvider<T extends string>({ children, csvConfig, skipHeaderRows = 1, parser }: ImportProviderProps<T>) {
    const { notification } = App.useApp();
    const [importErrors, setImportErrors] = useState<string[]>([]);
    const [importProgress, setImportProgress] = useState<ImportProgress>({ imported: 0, failed: 0, total: 0 });

    const [viewState, dispatch] = useReducer(reducer, initialViewState);

    const _importHasErrors = () => {
        // Check if any cell has static validation error
        const hasErrors = some(viewState.records, row =>
            some(row, item => {
                if (item.error) {
                    console.info(`Import Error: `, item);
                }
                return !!item.error;
            })
        );

        // Check if any cell has api validation error
        const hasImportErrors = some(importErrors, Boolean);

        return hasErrors || hasImportErrors;
    };

    const importHasErrors = _importHasErrors();
    const isReadyToImport = viewState.records.length > 0 && !importHasErrors;

    async function onFileLoad(file: RcFile) {
        try {
            console.info('ImportProvider.onFileLoad.file', file);

            const content = await readFile(file);
            const parsedRecords = await parseCsv(content, csvConfig, skipHeaderRows, parser);

            // Sort out rows with errors and warnings to the top
            const records = sortBy(parsedRecords, [
                row => !Object.values(row).some((cell: any) => cell.error),
                row => !Object.values(row).some((cell: any) => cell.warning),
            ]);

            dispatch({ type: 'preview', payload: { file, records } });
        } catch (err) {
            console.error('ImportProvider.onFileLoad', err);
            notification.warning({
                key: 'import-file-load-error-notification',
                message: `Cannot process file. Error: ${(err as Error).message}`,
            });
        }
    }

    function onFileRemove() {
        setImportErrors([]);
        dispatch({ type: 'reset' });
    }

    function onImportSubmit() {
        dispatch({ type: 'importing', payload: { records: viewState.records } });
    }

    function onImportComplete() {
        dispatch({ type: 'completed' });
    }

    const providerValue = {
        viewState,
        importProgress,
        setImportProgress,
        importErrors,
        setImportErrors,
        importHasErrors,
        isReadyToImport,
        csvConfig,
        onFileLoad,
        onFileRemove,
        onImportSubmit,
        onImportComplete,
    };

    return <ImportContext.Provider value={providerValue}>{children}</ImportContext.Provider>;
}

export function useImportContext() {
    const context = useContext(ImportContext);
    if (context === undefined) {
        throw new Error('useImportContext must be used within an ImportProvider');
    }
    return context;
}
